/**
 * Cinta de logos de las marcas que trabaja la ferreteria, de lado a lado de la
 * pantalla, corriendo sin pausa hacia la izquierda.
 *
 * Reemplaza a la lista de nombres, que con el catalogo real (mas de cien
 * marcas) ocupaba media portada. Muestra solo las marcas con logo cargado
 * (`configuracion/logos-marcas.ts`) que tengan algo publicado.
 *
 * La pista lleva la lista dos veces y se desplaza la mitad de su ancho: al
 * terminar, la segunda copia esta justo donde partio la primera y el salto no
 * se ve. Con pocas marcas la lista se repite antes, para que la mitad de la
 * pista siempre sea mas ancha que la pantalla. Solo la primera copia se lee y
 * se recorre con el teclado; las demas son relleno visual.
 *
 * Cada logo lleva a la busqueda por esa marca ("necesito una Makita"). Se
 * detiene con el puntero encima o con el foco del teclado adentro (`inicio.css`).
 */
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { logosVigentes, medidaEnCinta, type LogoVigente } from '../../utilidades/logos-marcas';
import { rutas } from '../../utilidades/rutas';
import { Imagen } from '../comunes/Imagen';

/** Logos que tiene que haber en media pista para cubrir una pantalla ancha. */
const MINIMO_POR_VUELTA = 12;
/** Segundos que tarda cada logo en pasar: fija la velocidad, no la vuelta. */
const SEGUNDOS_POR_LOGO = 3.5;

export interface CintaMarcasProps {
  marcas: string[];
}

function ListaLogos({ logos, relleno }: { logos: LogoVigente[]; relleno: boolean }) {
  return (
    <ul aria-hidden={relleno} inert={relleno}>
      {logos.map((logo, indice) => {
        const medida = medidaEnCinta(logo);
        return (
          <li key={`${logo.archivo}-${indice}`}>
            <Link to={rutas.busqueda(logo.termino)}>
              <Imagen
                src={`/marcas/${logo.archivo}`}
                alt={logo.nombre}
                ancho={medida.ancho}
                alto={medida.alto}
              />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

export function CintaMarcas({ marcas }: CintaMarcasProps) {
  const logos = logosVigentes(marcas);
  if (logos.length === 0) return null;

  const repeticiones = Math.ceil(MINIMO_POR_VUELTA / logos.length);
  const vuelta = Array.from({ length: repeticiones }, () => logos).flat();
  const duracion = `${vuelta.length * SEGUNDOS_POR_LOGO}s`;

  return (
    <section className="cinta-marcas" aria-labelledby="marcas">
      <h2 id="marcas" className="solo-lectores">
        Marcas que trabajamos
      </h2>
      <div className="cinta-marcas-pista" style={{ '--cinta-duracion': duracion } as CSSProperties}>
        <ListaLogos logos={logos} relleno={false} />
        {repeticiones > 1 ? <ListaLogos logos={vuelta.slice(logos.length)} relleno /> : null}
        <ListaLogos logos={vuelta} relleno />
      </div>
    </section>
  );
}
